import {
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Popover,
  Typography,
} from "@material-ui/core";
import React from "react";
import CloseIcon from "@material-ui/icons/Close";
import SearchIcon from "@material-ui/icons/Search";
import NotInterestedIcon from "@material-ui/icons/NotInterested";
import ControlledSelect from "../../../app/components/inputs/ControlledSelect";
import FnAccessWrapper from "../../../app/components/access-wrappers/FnAccessWrapper";
import {
  ProviderDto,
  providerToOptionDtos,
} from "../../provider/dtos/ProviderDto";
import {
  programTypeSummariesToOptions,
  ProgramTypeSummaryDto,
} from "../../program-types/dtos/ProgramTypeSummaryDto";
import { SnapGroupCycleSearchParamsDto } from "../dtos/SnapGroupCycleSearchParamsDto";
import { useSnapCycleFilter } from "../hooks/useSnapCycleFilter";
import { getSnapCycleGenderOptions } from "../enums/SnapGroupCycleGenderEnum";
import ControlledCheckbox from "../../../app/components/inputs/ControlledCheckbox";
import { useWatch } from "react-hook-form";
import ProgramControlledSelect from "../../programs/components/ProgramControlledSelect";
import { ProgramDto } from "../../programs/dtos/ProgramDto";
import ControlledInput from "../../../app/components/inputs/ControlledInput";
import { LocationDto, getLocationOptions } from "../../locations/dtos/LocationDto";

interface Props {
  onClose: () => void;
  params: SnapGroupCycleSearchParamsDto;
  onSearch: (dto: SnapGroupCycleSearchParamsDto) => void;
  anchorEl: HTMLButtonElement | null;
  providers: ProviderDto[];
  programTypes: ProgramTypeSummaryDto[];
  providerPrograms: { [key: string]: ProgramDto[] };
  providerLocations: { [key: string]: LocationDto[] };
  onProviderChange(providerId: string): void;
}

const SnapCycleFilter: React.FC<Props> = ({
  onClose,
  params,
  onSearch,
  anchorEl,
  providers,
  programTypes,
  providerPrograms,
  providerLocations,
  onProviderChange,  
}) => {
  //#region State
  const {
    state: { programs, locations },
    form: { control, clearAllFilters },
    handlers: { handleSubmit },
  } = useSnapCycleFilter(onSearch, 
        onClose, 
        onProviderChange,
        providerPrograms,
        providerLocations,
        params);

  const { fidelityRequired } = useWatch({ control });
  //#endregion

  const getSortedProviders = () => {
    return providers.sort((a, b) => (a.name > b.name ? 1 : -1));
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      anchorOrigin={{
        vertical: "center",
        horizontal: "center",
      }}
      onClose={onClose}
    >
      <div style={{ margin: "15px", width: "500px" }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item md={12} xs={12}>
              <Box display="flex">
                <Typography variant="h6">SNAP Cycle Filters</Typography>
                <Box marginLeft="auto">
                  <IconButton
                    onClick={clearAllFilters}
                    title="Clear All Filters"
                  >
                    <NotInterestedIcon />
                  </IconButton>
                  <IconButton onClick={onClose} title="Close Filter">
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>
            </Grid>

            {/* Program Types */}
            <Grid item md={12} xs={12}>
              <ControlledSelect
                control={control}
                name="programTypeId"
                label="Program Type"
                options={programTypeSummariesToOptions(programTypes, "All")}
                fullWidth
              />
            </Grid>

            {/* Provider */}
            <FnAccessWrapper>
              <Grid item md={12} xs={12}>
                <ControlledSelect
                  control={control}
                  name="providerId"
                  label="Provider"
                  fullWidth
                  options={providerToOptionDtos(
                    getSortedProviders(),
                    [],
                    "All"
                  )}
                />
              </Grid>
            </FnAccessWrapper>

            {/* Programs */}
            <Grid item md={12} xs={12}>
              <ProgramControlledSelect
                control={control}
                name="programId"
                label="Program"
                programs={programs}
                disabled={programs.length === 0}
                defaultOptionLabel={programs.length > 0 ? "All" : "No SNAP Programs"}
                isLoading={false}
              />
            </Grid>
            
            {/*Locations */}
            <Grid item md={12} xs={12}>
              <ControlledSelect
                control={control}
                name="locationId"
                label="Location"
                options={getLocationOptions(locations, programs.length > 0 ? "All" : "N/A")}
                disabled={locations.length === 0}
                fullWidth
              />
            </Grid>

            {/* Gender */}
            <Grid item md={12} xs={12}>
              <ControlledSelect
                control={control}
                name="gender"
                label="Gender"
                options={getSnapCycleGenderOptions("All")}
                fullWidth
              />
            </Grid>

            {/* Cycle Start Date Range */}
            <Grid item md={12} xs={12}>
              <Divider />
            </Grid>
            <Grid item md={12} xs={12}>
              <Typography color="primary">Group Start Date Range</Typography>
            </Grid>
            <Grid item md={6} xs={12}>
              <ControlledInput
                control={control}
                name="cycleStartDateRangeStart"
                label="Start Date"
                type="date"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>            
            <Grid item md={6} xs={12}>
              <ControlledInput
                control={control}
                name="cycleStartDateRangeEnd"
                label="End Date"
                type="date"
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </Grid>

            {/* Requires Fidelity */}
            <Grid item md={12} xs={12}>
              <ControlledCheckbox
                color="primary"
                control={control}
                name="fidelityRequired"
                label="Requires Fidelity"
                value={fidelityRequired}
              />
            </Grid>

            {/* Include Deleted */}
            <FnAccessWrapper>
            <Grid item md={12} xs={12}>
              <ControlledCheckbox
                color="primary"
                control={control}
                name="includeDeleted"
                label="Include Deleted"
              />
            </Grid>
            </FnAccessWrapper>           

            

            <Grid item md={12} xs={12}>
              <Button
                startIcon={<SearchIcon />}
                color="primary"
                variant="contained"
                fullWidth
                type="submit"
              >
                Search
              </Button>
            </Grid>
          </Grid>
        </form>
      </div>
    </Popover>
  );
};

export default SnapCycleFilter;
